import { cn } from "@fatbees/ui-utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { forwardRef } from "react";

export const inputVariants = cva(
  [
    "placeholder:text-muted-foreground",
    "selection:bg-primary",
    "selection:text-primary-foreground",
    "dark:bg-input/30",
    "border-input",
    "flex",
    "w-full",
    "min-w-0",
    "rounded-md",
    "border",
    "bg-transparent",
    "px-3",
    "text-base",
    "transition-[color,box-shadow]",
    "outline-none",
    "file:text-foreground",
    "file:inline-flex",
    "file:h-7",
    "file:border-0",
    "file:bg-transparent",
    "file:text-sm",
    "file:font-medium",
    "disabled:pointer-events-none",
    "disabled:cursor-not-allowed",
    "disabled:opacity-50",
    "aria-invalid:ring-destructive/20",
    "dark:aria-invalid:ring-destructive/40",
    "aria-invalid:border-destructive",
  ],
  {
    variants: {
      size: {
        lg: ["text-lg", "md:text-md"],
        md: ["h-9", "py-1", "text-md", "md:text-sm"],
        sm: ["h-8", "text-sm", "md:text-xs"],
      },
      border: {
        default: [
          "shadow-xs",
          "focus-visible:border-ring",
          "focus-visible:ring-ring/50",
          "focus-visible:ring-[2px]",
        ],
        faint: [
          "border-neutral-100",
          "focus-visible:border-ring",
          "focus-visible:ring-ring/50",
          "focus-visible:ring-[2px]",
        ],
        none: [
          "border-none",
          "shadow-none",
          "focus:ring-0",
          "focus:outline-none",
        ],
      },
      hasError: {
        false: "",
        true: [
          "border-secondary-300",
          "text-secondary-900",
          "placeholder-secondary-300",
          "focus:border-secondary-500",
          "focus:ring-secondary-500",
        ],
      },
    },
    defaultVariants: {
      border: "default",
      size: "md",
    },
  },
);

export interface InputProps
  extends
    Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  // This is provided by InputError
  errorId?: string;
}

export const Input = forwardRef(function Input(
  { errorId, className, hasError, size, type, ...props }: InputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  return (
    <input
      type={type}
      ref={ref}
      data-slot="input"
      className={cn(inputVariants({ ...props, size }), className)}
      aria-invalid={hasError ? true : undefined}
      aria-errormessage={errorId}
      {...props}
    />
  );
});
